import React from 'react'
import styled from '@emotion/styled'
import { BiometricsToggle } from '@mobi/account/Areas/Biometrics/Components/BiometricsToggle'
import { useBiometricsStatus } from '@mobi/account/Areas/Biometrics/Hooks/useBiometricsStatus'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { sendToNative } from '@mobi/web-native-comms/web'
import { MainContainer } from '@core/Components/Containers'
import { NoticeBox } from '@core/Components/NoticeBox'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'
import { useLogon } from '@core/Utils/hooks'
import {
  BiometricsPageSubHeaderStyled,
  ContentBodyStyled,
  GeneralSettingStyled,
} from './BiometricsPage.styles'
import { trackBiometricsToggle } from './helpers/analytics'

export const BiometricsPage = () => {
  const { accountNumber } = useLogon()
  return accountNumber ? <BiometricsPageContents accountNumber={accountNumber} /> : null
}

const BiometricsPageContents = ({ accountNumber }: { accountNumber: number }) => {
  const testId = { 'data-tid-biometrics-page-container': '' }
  const { isBiometricsStatusReady, isBiometricsEnabled } = useBiometricsStatus()
  const isBiometricsToggleOn = isBiometricsStatusReady ? isBiometricsEnabled : false

  // Currently we cannot turn on Biometric if MFA is enabled - https://rwwa.atlassian.net/browse/MOBI-3823

  const { data: cognitoUser, isLoading: isMFAUserLoading } = useCognitoUser(accountNumber)
  const preferredFactor = cognitoUser?.preferredFactor ?? 'Unknown'
  const isMFAEnabled = preferredFactor !== 'None' && preferredFactor !== 'Unknown'
  const isBlockedByMFA = isMFAUserLoading || !cognitoUser || isMFAEnabled

  return (
    <MainContainer {...testId}>
      <PageHeader title='Enable PIN/Biometrics' />

      <ContentBodyStyled>
        <GeneralSettingStyled>
          <BiometricsPageSubHeaderStyled>General Settings</BiometricsPageSubHeaderStyled>

          <BiometricsToggleWrapper>
            <BiometricsToggle
              toggleState={isBiometricsToggleOn}
              text='Use Face ID, Fingerprint or PIN to login'
              isDisabled={!isBiometricsStatusReady || (isBlockedByMFA && !isBiometricsToggleOn)}
              onToggle={() => {
                sendToNative('REQUEST_TOGGLE_BIOMETRICS')
                trackBiometricsToggle(!isBiometricsToggleOn)
              }}
              toggleTitle='Enable/Disable'
            />
          </BiometricsToggleWrapper>

          {!isMFAUserLoading && isMFAEnabled && (
            <NoticeBox
              title=''
              noticeBoxType='Warning'
              hasBorder
              data-testid='mfa-enabled-notice-box'
            >
              Biometrics can only be enabled from the log-in page for your account
            </NoticeBox>
          )}
        </GeneralSettingStyled>
      </ContentBodyStyled>
    </MainContainer>
  )
}

const BiometricsToggleWrapper = styled.div({
  '> div': { justifyContent: 'space-between' },
})
